import { devLog, devError } from '../utils/devLogger';
import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Badge, Button, Form, Spinner, Alert } from 'react-bootstrap';
import { useAuth } from '../auth/AuthContext';
import { db } from '../firebase';
import { collection, getDocs, query, where } from 'firebase/firestore';
import PlagiarismDetectionService from '../services/plagiarismDetection';
import FacilitatorNavbar from '../components/FacilitatorNavbar';
import '../styles/brand.css';

/**
 * Plagiarism Dashboard for Admins/Facilitators
 * Allows batch plagiarism checking across quizzes and assignments
 */
const PlagiarismDashboard = () => {
  const { currentUser, userProfile } = useAuth();
  const [assignments, setAssignments] = useState([]);
  const [quizzes, setQuizzes] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedType, setSelectedType] = useState('assignment'); // 'assignment' or 'quiz'
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [alert, setAlert] = useState({ show: false, message: '', type: '' });

  useEffect(() => {
    if (currentUser && ['admin', 'facilitator'].includes(userProfile?.role)) {
      loadData();
    }
  }, [currentUser, userProfile]);

  const loadData = async () => {
    try {
      setLoadingData(true);

      // Load assignments
      const assignmentsQuery = userProfile.role === 'admin'
        ? collection(db, 'assignments')
        : query(collection(db, 'assignments'), where('facilitatorId', '==', currentUser.uid));

      const assignmentsSnapshot = await getDocs(assignmentsQuery);
      const assignmentsData = assignmentsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setAssignments(assignmentsData);

      // Load quizzes
      const quizzesQuery = userProfile.role === 'admin'
        ? collection(db, 'quizzes')
        : query(collection(db, 'quizzes'), where('createdBy', '==', currentUser.uid));

      const quizzesSnapshot = await getDocs(quizzesQuery);
      const quizzesData = quizzesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setQuizzes(quizzesData);

    } catch (error) {
      devError('Error loading data:', error);
      showAlert('Error loading assignments and quizzes', 'danger');
    } finally {
      setLoadingData(false);
    }
  };

  const showAlert = (message, type) => {
    setAlert({ show: true, message, type });
    setTimeout(() => setAlert({ show: false, message: '', type: '' }), 5000);
  };

  const runPlagiarismCheck = async () => {
    if (!selectedId) {
      showAlert('Please select an assignment or quiz', 'warning');
      return;
    }

    setLoading(true);
    setResults([]);

    try {
      const plagiarismService = new PlagiarismDetectionService();
      const checkResults = await plagiarismService.batchCheckPlagiarism(selectedId);

      setResults(checkResults);

      const flaggedCount = checkResults.filter(r => r.flagged).length;
      if (flaggedCount > 0) {
        showAlert(`Plagiarism check complete. ${flaggedCount} submission(s) flagged.`, 'warning');
      } else {
        showAlert(`Plagiarism check complete. All ${checkResults.length} submission(s) are clear.`, 'success');
      }

    } catch (error) {
      devError('Batch plagiarism check error:', error);
      showAlert('Error running plagiarism check: ' + error.message, 'danger');
    } finally {
      setLoading(false);
    }
  };

  const getSeverityBadge = (percentage) => {
    const score = parseFloat(percentage);
    if (score >= 70) return <Badge bg="danger">High Risk</Badge>;
    if (score >= 50) return <Badge bg="warning">Medium Risk</Badge>;
    if (score >= 30) return <Badge bg="info">Low Risk</Badge>;
    return <Badge bg="success">Clear</Badge>;
  };

  if (!['admin', 'facilitator'].includes(userProfile?.role)) {
    return (
      <Container className="mt-5">
        <Alert variant="danger">
          <h4>Access Denied</h4>
          <p>You do not have permission to access the plagiarism dashboard.</p>
        </Alert>
      </Container>
    );
  }

  return (
    <>
      <FacilitatorNavbar currentPage="plagiarism" />

      {/* Hero Section */}
      <section className="hero-section">
        <Container>
          <Row>
            <Col lg={8}>
              <h1 className="mb-3">Plagiarism Detection Dashboard</h1>
              <p className="mission-text mb-4">
                Maintain academic integrity with automated plagiarism detection
              </p>
              <p className="mb-0">
                Check submissions for text similarity using advanced TF-IDF and cosine similarity algorithms.
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Main Content */}
      <Container className="py-5">
        {alert.show && (
          <Alert variant={alert.type} dismissible onClose={() => setAlert({ show: false, message: '', type: '' })}>
            {alert.message}
          </Alert>
        )}

        {loadingData ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3">Loading assignments and quizzes...</p>
          </div>
        ) : (
          <>
            {/* Selection Panel */}
            <Card className="mb-4 shadow-sm">
              <Card.Body>
                <Row>
                  <Col md={3}>
                    <Form.Group className="mb-3 mb-md-0">
                      <Form.Label>Type</Form.Label>
                      <Form.Select
                        value={selectedType}
                        onChange={(e) => {
                          setSelectedType(e.target.value);
                          setSelectedId(null);
                          setResults([]);
                        }}
                      >
                        <option value="assignment">Assignment</option>
                        <option value="quiz">Quiz</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3 mb-md-0">
                      <Form.Label>Select {selectedType === 'assignment' ? 'Assignment' : 'Quiz'}</Form.Label>
                      <Form.Select
                        value={selectedId || ''}
                        onChange={(e) => {
                          setSelectedId(e.target.value);
                          setResults([]);
                        }}
                      >
                        <option value="">Choose...</option>
                        {selectedType === 'assignment'
                          ? assignments.map(assignment => (
                              <option key={assignment.id} value={assignment.id}>
                                {assignment.title}
                              </option>
                            ))
                          : quizzes.map(quiz => (
                              <option key={quiz.id} value={quiz.id}>
                                {quiz.title}
                              </option>
                            ))
                        }
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={3} className="d-flex align-items-end">
                    <Button
                      variant="primary"
                      onClick={runPlagiarismCheck}
                      disabled={!selectedId || loading}
                      className="w-100"
                    >
                      {loading ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Checking...
                        </>
                      ) : (
                        'Run Plagiarism Check'
                      )}
                    </Button>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Info Cards */}
            {results.length > 0 && (
              <Row className="mb-4">
                <Col md={3}>
                  <Card className="text-center shadow-sm">
                    <Card.Body>
                      <h2 className="display-4 text-primary">{results.length}</h2>
                      <p className="text-muted mb-0">Total Submissions</p>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="text-center shadow-sm">
                    <Card.Body>
                      <h2 className="display-4 text-danger">
                        {results.filter(r => r.flagged).length}
                      </h2>
                      <p className="text-muted mb-0">Flagged</p>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="text-center shadow-sm">
                    <Card.Body>
                      <h2 className="display-4 text-success">
                        {results.filter(r => !r.flagged).length}
                      </h2>
                      <p className="text-muted mb-0">Clear</p>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="text-center shadow-sm">
                    <Card.Body>
                      <h2 className="display-4 text-info">
                        {results.length > 0
                          ? (results.reduce((sum, r) => sum + (r.similarityScore || 0), 0) / results.length * 100).toFixed(1)
                          : 0}%
                      </h2>
                      <p className="text-muted mb-0">Avg. Similarity</p>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            )}

            {/* Results Table */}
            {results.length > 0 && (
              <Card className="shadow-sm">
                <Card.Header>
                  <h4 className="mb-0">Plagiarism Check Results</h4>
                </Card.Header>
                <Card.Body>
                  <div className="table-responsive">
                    <Table striped hover>
                      <thead>
                        <tr>
                          <th>Student</th>
                          <th>Status</th>
                          <th>Max Similarity</th>
                          <th>Severity</th>
                          <th>Matches</th>
                          <th>Compared</th>
                        </tr>
                      </thead>
                      <tbody>
                        {results
                          .sort((a, b) => (b.similarityScore || 0) - (a.similarityScore || 0))
                          .map((result, idx) => (
                            <tr key={idx} className={result.flagged ? 'table-warning' : ''}>
                              <td>
                                <strong>{result.userName || result.userId}</strong>
                                {result.error && (
                                  <div>
                                    <small className="text-danger">Error: {result.error}</small>
                                  </div>
                                )}
                              </td>
                              <td>
                                {result.flagged ? (
                                  <Badge bg="danger">Flagged</Badge>
                                ) : (
                                  <Badge bg="success">Clear</Badge>
                                )}
                              </td>
                              <td>
                                <strong>{result.maxSimilarityPercentage || '0.0'}%</strong>
                              </td>
                              <td>
                                {getSeverityBadge(result.maxSimilarityPercentage || 0)}
                              </td>
                              <td>
                                {result.matches?.length || 0}
                                {result.matches && result.matches.length > 0 && (
                                  <details className="mt-1">
                                    <summary className="cursor-pointer text-primary small">
                                      View Details
                                    </summary>
                                    <ul className="mt-2 small">
                                      {result.matches.map((match, midx) => (
                                        <li key={midx}>
                                          {match.userName}: {(match.score * 100).toFixed(1)}%
                                        </li>
                                      ))}
                                    </ul>
                                  </details>
                                )}
                              </td>
                              <td>{result.totalCompared || 0}</td>
                            </tr>
                          ))}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            )}

            {/* Empty State */}
            {!loading && results.length === 0 && (
              <Card className="text-center py-5">
                <Card.Body>
                  <div className="text-muted">
                    <i className="bi bi-search display-1"></i>
                    <h4 className="mt-3">No Results Yet</h4>
                    <p>Select an assignment or quiz and click "Run Plagiarism Check" to get started.</p>
                  </div>
                </Card.Body>
              </Card>
            )}

            {/* How it Works */}
            <Card className="mt-4 shadow-sm">
              <Card.Header>
                <h5 className="mb-0">How Plagiarism Detection Works</h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={4}>
                    <h6>1. Text Normalization</h6>
                    <p className="small text-muted">
                      Submissions are normalized by removing punctuation, converting to lowercase,
                      and standardizing whitespace.
                    </p>
                  </Col>
                  <Col md={4}>
                    <h6>2. Similarity Analysis</h6>
                    <p className="small text-muted">
                      Two algorithms are used: Dice coefficient (string similarity) and
                      TF-IDF cosine similarity (semantic similarity).
                    </p>
                  </Col>
                  <Col md={4}>
                    <h6>3. Flagging Criteria</h6>
                    <p className="small text-muted">
                      Submissions with 70% or higher average similarity are automatically
                      flagged for review.
                    </p>
                  </Col>
                </Row>
                <hr />
                <div className="small text-muted">
                  <strong>Note:</strong> This tool compares submissions within the same assignment/quiz only.
                  It does not check against external sources or previous submissions from other assignments.
                  Manual review is recommended for all flagged submissions.
                </div>
              </Card.Body>
            </Card>
          </>
        )}
      </Container>
    </>
  );
};

export default PlagiarismDashboard;
